import React, { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");

  const navItems = [
    { label: "Home", id: "hero" },
    { label: "Sobre", id: "about" },
    { label: "Áreas de Atuação", id: "areas" },
    { label: "Projetos", id: "projects" },
    { label: "Parceiros", id: "partners" },
    { label: "Contato", id: "contact" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);

      const sections = navItems
        .map((item) => ({
          id: item.id,
          element: document.getElementById(item.id),
        }))
        .filter((item) => item.element);

      let currentSection = "hero";

      sections.forEach(({ id, element }) => {
        const rect = element.getBoundingClientRect();

        if (rect.top <= 140) {
          currentSection = id;
        }
      });

      setActiveSection(currentSection);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);

    if (element) {
      const headerOffset = 96;

      const elementPosition =
        element.getBoundingClientRect().top + window.pageYOffset;

      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }

    setIsMenuOpen(false);
  };

  return (
    <>
      <header
        className={`
          fixed top-0 left-0 right-0 z-50
          transition-all duration-300
          ${isScrolled
            ? "bg-white/95 backdrop-blur-xl shadow-sm border-b border-black/5"
            : "bg-white/80 backdrop-blur-md"
          }
        `}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div
            className={`
              flex items-center justify-between
              transition-all duration-300
              ${isScrolled ? "h-[82px]" : "h-[100px]"}
            `}
          >
            {/* Logo */}
            <button
              type="button"
              onClick={() => scrollToSection("hero")}
              className="
                flex items-center
                shrink-0
                focus:outline-none
                focus-visible:ring-2
                focus-visible:ring-green-600
                focus-visible:ring-offset-4
                rounded-lg
              "
              aria-label="Ir para o início"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_40c86c6d-2b74-4d54-8e62-bf8382c59aa6/artifacts/4bpiwzen_0cf8f0f6-8c2c-4700-b404-8a52d39933d6.jpeg"
                alt="Instituto Mercosul Amazônia - IMA"
                className={`
                  w-auto object-contain
                  transition-all duration-300
                  ${isScrolled
                    ? "h-[58px] md:h-[62px]"
                    : "h-[68px] md:h-[76px]"
                  }
                `}
              />
            </button>

            {/* Desktop */}
            <div className="hidden lg:flex items-center gap-7">
              <nav
                className="flex items-center gap-1"
                aria-label="Navegação principal"
              >
                {navItems
                  .filter((item) => item.id !== "contact")
                  .map((item) => {
                    const isActive = activeSection === item.id;

                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => scrollToSection(item.id)}
                        className={`
                          relative
                          px-3 py-3
                          text-[14px]
                          font-medium
                          whitespace-nowrap
                          transition-colors duration-200
                          ${isActive
                            ? "text-green-700"
                            : "text-slate-600 hover:text-green-700"
                          }
                        `}
                      >
                        {item.label}

                        <span
                          className={`
                            absolute
                            left-3 right-3
                            bottom-[5px]
                            h-[2px]
                            rounded-full
                            bg-green-600
                            transition-all duration-300
                            ${isActive
                              ? "opacity-100 scale-x-100"
                              : "opacity-0 scale-x-0"
                            }
                          `}
                        />
                      </button>
                    );
                  })}
              </nav>

              {/* CTA Contato */}
              <button
                type="button"
                onClick={() => scrollToSection("contact")}
                className="
                  inline-flex
                  items-center
                  gap-2
                  bg-green-700
                  hover:bg-green-800
                  text-white
                  text-sm
                  font-semibold
                  px-5
                  py-3
                  rounded-full
                  shadow-sm
                  hover:shadow-md
                  transition-all
                  duration-200
                  group
                "
              >
                Fale com o IMA

                <ArrowUpRight
                  size={16}
                  className="
                    transition-transform
                    duration-200
                    group-hover:translate-x-0.5
                    group-hover:-translate-y-0.5
                  "
                />
              </button>
            </div>

            {/* Mobile */}
            <button
              type="button"
              className="
                lg:hidden
                flex items-center justify-center
                w-11 h-11
                rounded-full
                border border-black/10
                bg-white
                text-slate-800
                shadow-sm
                transition
                hover:bg-slate-50
              "
              onClick={() => setIsMenuOpen((prev) => !prev)}
              aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`
            lg:hidden
            overflow-hidden
            bg-white
            border-t border-black/5
            transition-all duration-300 ease-in-out
            ${isMenuOpen
              ? "max-h-[620px] opacity-100"
              : "max-h-0 opacity-0 border-t-transparent"
            }
          `}
        >
          <nav
            className="max-w-7xl mx-auto px-5 py-5"
            aria-label="Navegação mobile"
          >
            <div className="flex flex-col">
              {navItems
                .filter((item) => item.id !== "contact")
                .map((item) => {
                  const isActive = activeSection === item.id;

                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => scrollToSection(item.id)}
                      className={`
                        flex items-center
                        justify-between
                        py-4
                        border-b border-slate-100
                        text-left
                        text-[16px]
                        font-medium
                        transition-colors
                        ${isActive
                          ? "text-green-700"
                          : "text-slate-700 hover:text-green-700"
                        }
                      `}
                    >
                      <span>{item.label}</span>

                      {isActive && (
                        <span className="w-2 h-2 rounded-full bg-green-600" />
                      )}
                    </button>
                  );
                })}

              <button
                type="button"
                onClick={() => scrollToSection("contact")}
                className="
                  mt-5
                  w-full
                  inline-flex
                  justify-center
                  items-center
                  gap-2
                  bg-green-700
                  hover:bg-green-800
                  text-white
                  font-semibold
                  px-5
                  py-4
                  rounded-xl
                  transition
                  group
                "
              >
                Fale com o IMA

                <ArrowUpRight
                  size={17}
                  className="
                    transition-transform
                    group-hover:translate-x-0.5
                    group-hover:-translate-y-0.5
                  "
                />
              </button>
            </div>
          </nav>
        </div>
      </header>

      {/* Overlay mobile */}
      {isMenuOpen && (
        <button
          type="button"
          aria-label="Fechar menu"
          onClick={() => setIsMenuOpen(false)}
          className="
            fixed inset-0
            z-40
            bg-black/20
            backdrop-blur-[2px]
            lg:hidden
          "
        />
      )}
    </>
  );
};

export default Header;